export type ShotId = 'single-drone-realtime' | 'fleet-overview' | 'alert-thresholds' | 'audit-log';

type Shot = {
  /** 最大生成宽度的固有像素尺寸，用来写 width/height 属性，避免图片加载时的布局位移。 */
  width: number;
  height: number;
  widths: number[];
  alt: string;
  caption: string;
};

/**
 * 演示环境（demo.havorix.work）的真实截图，不是设计稿。
 * 三个宽图按 800 / 1200 / 1800 三档出图，竖版机队总览到原生宽度 561 为止。
 * 审计日志那张的来源 IP 列在出图前已打码，重新出图时不要漏掉这一步。
 */
export const SHOTS: Record<ShotId, Shot> = {
  'single-drone-realtime': {
    width: 1800,
    height: 842,
    widths: [800, 1200, 1800],
    alt: '单机管理页：左上角为实时姿态与遥测指标，中部为地图上的实时航迹，右侧为链路质量、适配器与任务状态',
    caption:
      '单机管理页。左侧为机体上报的姿态与遥测数据，中部地图上的航迹由浏览器实时记录且不入库，右侧为链路质量与任务状态。',
  },
  'fleet-overview': {
    width: 561,
    height: 680,
    widths: [420, 561],
    alt: '机队总览：地图上的机体位置标记与右侧的无人机卡片，卡片呈现电量、信号、卫星数、高度与地速',
    caption: '机队总览。一张地图配一列卡片，单击卡片选中机体，双击进入单机管理。',
  },
  'alert-thresholds': {
    width: 1800,
    height: 685,
    widths: [800, 1200, 1800],
    alt: '默认告警阈值页：逐条列出电量、卫星数、链路质量、单体电压、定位精度、链路延迟等指标的三级阈值，下方为概览频率与审计保留天数',
    caption:
      '默认告警阈值页。每项指标各自定义注意、警告、严重三级，并针对上下限方向分别校验；未单独配置的机体直接继承该默认值。',
  },
  'audit-log': {
    width: 1800,
    height: 723,
    widths: [800, 1200, 1800],
    alt: '审计日志页：按时间倒序列出登录与令牌签发记录，含操作人、目标机体与操作类型；来源 IP 一列已打码',
    caption:
      '审计日志页。登录、令牌签发与指令下发逐条记录操作人、目标与时间，默认保留 180 天；图中来源 IP 一列已打码。',
  },
};

export function shotSrcSet(id: ShotId, format: 'avif' | 'webp'): string {
  return SHOTS[id].widths.map((width) => `/screenshots/${id}-${width}.${format} ${width}w`).join(', ');
}

/** `<picture>` 不被支持时的兜底，取最大宽度的那一档。 */
export function shotFallback(id: ShotId): string {
  const widths = SHOTS[id].widths;
  return `/screenshots/${id}-${widths[widths.length - 1]}.webp`;
}

/** 满宽插图（落在 .container-page 里）的 sizes；窄屏是可横向滚动的固定宽度，所以按 34rem 算。 */
export const SHOT_SIZES_FULL = '(min-width: 1280px) 1152px, (min-width: 768px) calc(100vw - 4rem), 34rem';

/** 侧栏插图的 sizes：lg 以上固定在 20rem 栏内，以下撑满整行。 */
export const SHOT_SIZES_ASIDE =
  '(min-width: 1024px) 320px, (min-width: 768px) calc(100vw - 4rem), calc(100vw - 2.5rem)';
